import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { applyFilters, parseFilters } from "@/lib/filters";

import { IrtCell } from "./Irt";
import { RefreshButton } from "./RefreshButton";
import { UptimeDetail } from "./UptimeDetail";
import { getStockTab, getUptimeTab, messageFor, STOCK_QUERY_KEY, UPTIME_QUERY_KEY, type UptimeRow } from "./api";
import {
  createFailed, filterUptimeRows, IRT_FILTER, irtKey, irtLine, irtShown, linkedSelection, marketplaceLabel, orderUptimeRows,
  parisDateTime, shownVerdict, storeLabel, uptimeBanner, uptimeCheckedLine, uptimeDeepLinkMissing, uptimeDeepLinkTarget, uptimeKey,
  uptimeTone,
} from "./format";
import { useRefresh } from "./useRefresh";

const PAGE_SIZE = 50;

function options(values: string[]): Array<{ value: string; label: string }> {
  return values.map((v) => ({ value: v, label: v }));
}

/** One result per SKU, store and marketplace, sorted problems first; the status word always travels (REQ-UPT-001, 004). */
export { uptimeKey };

export function UptimeTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const values = parseFilters(search);
  const filterKey = JSON.stringify(values);
  // null until the operator pages or filters: a deep link then chooses the first page shown (the Stock tab's rule).
  const [pageIndex, setPageIndex] = useState<number | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [linkDismissed, setLinkDismissed] = useState(false);

  const query = useQuery({ queryKey: UPTIME_QUERY_KEY, queryFn: getUptimeTab });
  // The module's head query (useOpsMeta) holds the Stock tab answer; read it from the cache, never fetch it again here.
  const stock = useQuery({ queryKey: STOCK_QUERY_KEY, queryFn: getStockTab, enabled: false });
  const stockArtifact = stock.data?.data.artifact ?? null;
  const artifact = query.data?.data.artifact ?? null;
  const latestRun = query.data?.data.latest_run ?? null;
  // A manual refresh is a full run (the engine checks purchasability on every run); the Xero button belongs to the Stock tab.
  const refresh = useRefresh(areaName, latestRun, false);

  // C2 v1.7: the verdict each result paints (its own, or the stock-out's on an out-of-stock listing); the order and the
  // IRT filter read the same verdict, so a borrowed NEW ISSUE sorts and filters as one.
  const irtSegment = artifact?.segments.irt;
  const irtOn = irtShown(irtSegment);
  const verdictOf = (row: UptimeRow) => shownVerdict(row, artifact, stockArtifact)?.verdict;
  // A link that keeps `f_irt` while no verdict shows would hide every row behind a filter the bar cannot clear.
  const shownValues = irtOn ? values : { ...values, irt: null };
  const rows = useMemo(
    () => orderUptimeRows(filterUptimeRows(artifact?.rows ?? [], shownValues, verdictOf), verdictOf),
    [artifact, stockArtifact, filterKey],
  );
  // C6 v1.7 D5: a link `?sku=&store=&marketplace=` opens the named result with its detail and marks it in the table.
  const linked = uptimeDeepLinkTarget(rows, search.sku, search.store, search.marketplace, PAGE_SIZE);
  const hiddenByFilters = !linked && uptimeDeepLinkTarget(orderUptimeRows(artifact?.rows ?? [], verdictOf), search.sku, search.store, search.marketplace, PAGE_SIZE) != null;
  const linkMissing = artifact && !linked ? uptimeDeepLinkMissing(search.sku, search.store, search.marketplace, hiddenByFilters) : null;
  const lastPage = Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1);
  const page = Math.min(pageIndex ?? linked?.pageIndex ?? 0, lastPage);
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const tiles = artifact?.tiles ?? null;
  const banner = uptimeBanner(artifact, latestRun, { stalledRunId: refresh.stalledRunId });
  const checkedLine = uptimeCheckedLine(artifact);
  const irtSummary = irtLine(irtSegment, artifact?.trigger_type);

  const filters: FilterSpec[] = [
    { name: "search", label: "Search SKU or product", kind: "text" },
    { name: "product", label: "Product", kind: "choice", options: options(artifact?.filters.products ?? []) },
    {
      name: "store", label: "Store", kind: "choice",
      options: (artifact?.filters.channel_locations ?? []).map((cl) => {
        const row = artifact?.rows.find((r) => r.channel_location === cl);
        return { value: cl, label: row ? storeLabel(row) : cl };
      }),
    },
    { name: "marketplace", label: "Marketplace", kind: "choice", options: options(artifact?.filters.marketplaces ?? []) },
    { name: "status", label: "Status", kind: "choice", options: options(artifact?.filters.statuses ?? []) },
  ];
  if (irtOn) filters.push({ name: "irt", label: "IRT", kind: "choice", options: IRT_FILTER });

  const columns: ColumnDef<UptimeRow, unknown>[] = [
    {
      id: "sku", header: "SKU",
      cell: ({ row }) => (
        <button type="button" data-nonmutating className="text-left text-ks-ink underline-offset-2 hover:underline"
          onClick={() => setSelected(uptimeKey(row.original))}>
          <span className="font-medium">{row.original.sku}</span>
          <span className="block text-xs text-muted-foreground">{row.original.name}</span>
        </button>
      ),
    },
    { id: "store", header: "Store", cell: ({ row }) => storeLabel(row.original) },
    { id: "marketplace", header: "Marketplace", cell: ({ row }) => marketplaceLabel(row.original) },
    {
      id: "status", header: "Status",
      cell: ({ row }) => (
        <span>
          <Badge variant={uptimeTone(row.original.status)}>{row.original.status}</Badge>
          {row.original.reason ? <span className="block text-xs text-muted-foreground">{row.original.reason}</span> : null}
        </span>
      ),
    },
    { id: "read", header: "Listing read", cell: ({ row }) => parisDateTime(row.original.read_at) ?? "" },
  ];
  // Not before C2 v1.7, and not while the step is not set up (every row would read "not checked"; the line says it once).
  if (irtOn) {
    columns.push({
      id: "irt", header: "IRT",
      cell: ({ row }) => {
        const shown = shownVerdict(row.original, artifact, stockArtifact);
        return <IrtCell verdict={shown?.verdict} via={shown?.via} createFailed={createFailed(irtSegment, irtKey(row.original))} />;
      },
    });
  }

  const selectedKey = linkedSelection(selected, linkDismissed, linked?.key);
  const selectedRow = artifact?.rows.find((r) => uptimeKey(r) === selectedKey) ?? null;
  // The line under the banner speaks of Unknown rows: only when the listing view was dark.
  const darkView = artifact?.segments.uptime?.status === "failed";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        {banner.kind !== "ok" ? (
          <Alert variant={banner.kind === "failed" ? "destructive" : "default"} className="max-w-3xl">
            <AlertTitle>{banner.text}</AlertTitle>
            {banner.kind === "partial" && darkView ? <AlertDescription>An Unknown row is a check that could not be made, never a listing that is live.</AlertDescription> : null}
          </Alert>
        ) : <div />}
        <RefreshButton
          state={refresh.state}
          message={refresh.message}
          progress={refresh.progress}
          xeroAvailable={false}
          onStart={(source) => void refresh.start(source)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          ["Live", tiles?.live ?? "", null],
          ["Not live", tiles?.not_live ?? "", null],
          ["Unknown", tiles?.unknown ?? "", null],
          // The engine's two coverage counts (C2 v1.6 R1-5): the results, and the SKU x store rows they cover.
          ["Checked", tiles?.checked ?? "", tiles ? `${tiles.combinations} SKU x store` : null],
        ].map(([label, value, under]) => (
          <Card key={String(label)}>
            <CardHeader className="pb-1"><CardTitle className="text-sm font-normal">{label}</CardTitle></CardHeader>
            <CardContent className="text-2xl font-bold">
              {value}
              {under ? <span className="block text-xs font-normal text-muted-foreground">{under}</span> : null}
            </CardContent>
          </Card>
        ))}
      </div>

      {checkedLine ? <p className="text-sm text-muted-foreground">{checkedLine}</p> : null}
      {irtSummary ? <p className="text-sm text-muted-foreground">{irtSummary}</p> : null}
      {linkMissing ? <p role="status" className="text-sm text-ks-ink">{linkMissing}</p> : null}

      <div>
          <FilterBar
            filters={filters}
            values={values}
            onChange={(next) => {
              setPageIndex(0);
              const navigateOptions: Record<string, unknown> = {
                search: (prev: Record<string, unknown>) => applyFilters(prev, next),
                replace: true,
              };
              void navigate(navigateOptions as never);
            }}
          />
          {query.isError ? (
            <p role="alert" className="text-sm text-ks-danger">{messageFor(query.error, areaName)}</p>
          ) : (
            <DataTable
              columns={columns}
              rows={pageRows}
              page={{ pageIndex: page, pageSize: PAGE_SIZE, rowCount: rows.length }}
              onPageChange={setPageIndex}
              rowClassName={(r) => (linked && uptimeKey(r) === linked.key ? "ring-2 ring-inset ring-ks-purple" : undefined)}
              pending={query.isLoading}
              emptyMessage={artifact ? "No results match these filters." : "No pass has completed yet."}
            />
          )}
          <p className="mt-2 text-sm text-muted-foreground">Select a SKU to see the feed's flags behind the status.</p>
      </div>
      <UptimeDetail
        row={selectedRow}
        shown={selectedRow ? shownVerdict(selectedRow, artifact, stockArtifact) : undefined}
        createFailed={selectedRow ? createFailed(irtSegment, irtKey(selectedRow)) : false}
        onClose={() => { setSelected(null); setLinkDismissed(true); }}
      />
    </div>
  );
}
